import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Alert, Box, Button, MenuItem, Paper, Stack, TextField, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PropTypes from "prop-types";

export default function StudentServiceRequest() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const user = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("user"));
    } catch {
      return null;
    }
  }, []);
  const [student, setStudent] = useState(null);
  const [acyearId, setAcyearId] = useState("");
  const [academicYearLabel, setAcademicYearLabel] = useState("");
  const [types, setTypes] = useState([]);
  const [names, setNames] = useState([]);
  const [charges, setCharges] = useState([]);
  const [chargesLoading, setChargesLoading] = useState(false);
  const [typeId, setTypeId] = useState("");
  const [serviceName, setServiceName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [requestedRows, setRequestedRows] = useState([]);

  const auth = () => ({ Authorization: `Bearer ${sessionStorage.getItem("token")}` });
  const collegeId = user?.college_id;
  const clientId = user?.client_id;
  const accountEmail = user?.user_email || user?.email;

  const activeNames = useMemo(
    () =>
      names.filter(
        (row) =>
          String(row.service_type_id) === String(typeId) &&
          row.status === "ACTIVE" &&
          row.service_name
      ),
    [names, typeId]
  );
  const availableNames = useMemo(() => activeNames.map((row) => row.service_name), [activeNames]);
  const selectedType = types.find((row) => String(row.service_type_id) === String(typeId));
  const charge = charges.find(
    (row) =>
      String(row.service_type_id) === String(typeId) &&
      row.service_name === serviceName &&
      row.status === "ACTIVE"
  );
  const gridRows = charge
    ? [
        {
          id: charge.service_charge_id,
          service_type: selectedType?.service_type || "",
          service_name: charge.service_name,
          amount: Number(charge.amount),
          service_charge_id: charge.service_charge_id,
        },
      ]
    : [];

  const loadSetup = useCallback(async () => {
    if (!clientId || !collegeId) return;
    setLoading(true);
    try {
      const [typeRes, nameRes, yearRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/student-services/types/${clientId}/${collegeId}`, {
          headers: auth(),
        }),
        axios.get(`${API_BASE_URL}/api/student-services/names/${clientId}/${collegeId}`, {
          headers: auth(),
        }),
        axios.get(`${API_BASE_URL}/api/acyear/by-college/${clientId}/${collegeId}`, {
          headers: auth(),
        }),
      ]);
      const nameRows = nameRes.data?.data || [];

      setNames(nameRows);
      setTypes(
        (typeRes.data?.data || []).filter(
          (row) =>
            row.status === "ACTIVE" &&
            nameRows.some(
              (name) =>
                String(name.service_type_id) === String(row.service_type_id) &&
                name.status === "ACTIVE"
            )
        )
      );
      const yearRows = yearRes.data?.data || [];
      const currentYear = yearRows.find((row) => Number(row.is_active) === 1);
      if (!currentYear?.acyear_id)
        throw new Error("No active academic year is configured for this college");
      setAcyearId(currentYear.acyear_id);
      setAcademicYearLabel(currentYear.academic_year || String(currentYear.acyear_id));
      setError("");
    } catch (e) {
      setError(e.response?.data?.message || e.message || "Unable to load service options");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, clientId, collegeId]);

  useEffect(() => {
    loadSetup();
  }, [loadSetup]);
  useEffect(() => {
    if (!clientId || !collegeId || !acyearId) {
      setCharges([]);
      return;
    }
    setChargesLoading(true);
    axios
      .get(`${API_BASE_URL}/api/student-services/charges/${clientId}/${collegeId}`, {
        headers: auth(),
      })
      .then((res) =>
        setCharges(
          (res.data?.data || []).filter(
            (row) => String(row.acyear_id) === String(acyearId) && row.status === "ACTIVE"
          )
        )
      )
      .catch((e) => setError(e.response?.data?.message || "Unable to load service charges"))
      .finally(() => setChargesLoading(false));
  }, [API_BASE_URL, clientId, collegeId, acyearId]);

  useEffect(() => {
    if (!clientId || (!accountEmail && !user?.user_id)) {
      setError("Your login is missing its account details. Sign out and sign in again.");
      return;
    }
    axios
      .get(
        `${API_BASE_URL}/api/students/by-email/${clientId}/${encodeURIComponent(
          accountEmail || "_"
        )}?college_id=${encodeURIComponent(collegeId || "")}&user_id=${encodeURIComponent(
          user?.user_id || ""
        )}`,
        { headers: auth() }
      )
      .then((res) => {
        setStudent(res.data?.data || res.data || null);
        setError("");
      })
      .catch((e) => {
        setError(
          e.response?.status === 404
            ? "This login is not linked to an active student record for the selected college. Check the student account and college assignment."
            : e.response?.data?.message || "Unable to identify the signed-in student"
        );
      });
  }, [API_BASE_URL, clientId, collegeId, accountEmail, user?.user_id]);

  const loadRequests = useCallback(async () => {
    if (!clientId || !collegeId || !student?.student_id) return;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/student-service-application/${clientId}/${collegeId}/${student.student_id}`,
        { headers: auth() }
      );
      setRequestedRows(res.data?.data || []);
    } catch (e) {
      setError(e.response?.data?.message || "Unable to load requested services");
    }
  }, [API_BASE_URL, clientId, collegeId, student]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const removeRequest = async (row) => {
    const confirmation = await Swal.fire({
      title: "Delete service request?",
      text: "This request is unpaid and can be deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d32f2f",
    });
    if (!confirmation.isConfirmed) return;
    try {
      await axios.delete(
        `${API_BASE_URL}/api/student-service-application/${clientId}/${collegeId}/${row.id}`,
        { headers: auth() }
      );
      await loadRequests();
    } catch (e) {
      setError(e.response?.data?.message || "Unable to delete request");
    }
  };

  const editRequest = async (row) => {
    const options = Object.fromEntries(
      names
        .filter(
          (item) =>
            String(item.service_type_id) === String(row.service_type_id) && item.status === "ACTIVE"
        )
        .map((item) => [String(item.service_name_id), item.service_name])
    );
    const result = await Swal.fire({
      title: "Edit service request",
      input: "select",
      inputOptions: options,
      inputValue: String(row.service_name_id),
      inputPlaceholder: "Select service name",
      showCancelButton: true,
      confirmButtonText: "Update",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#1976d2",
      cancelButtonColor: "#d32f2f",
    });
    if (!result.isConfirmed || !result.value) return;
    try {
      await axios.put(
        `${API_BASE_URL}/api/student-service-application/${clientId}/${collegeId}/${row.id}`,
        { service_name_id: result.value, modified_by: user?.user_email || null },
        { headers: auth() }
      );
      await loadRequests();
    } catch (e) {
      setError(e.response?.data?.message || "Unable to update service request");
    }
  };

  const apply = async (row) => {
    if (!student?.student_id) {
      setError(
        "Your login is not linked to an active student record. Sign out and sign in again, or contact the college office."
      );
      return;
    }
    const confirmation = await Swal.fire({
      title: "Submit service request?",
      text: `Apply for ${row.service_type} / ${row.service_name} for ₹${row.amount}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Apply",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#1976d2",
      cancelButtonColor: "#d32f2f",
    });
    if (!confirmation.isConfirmed) return;
    try {
      const result = await axios.post(
        `${API_BASE_URL}/api/student-service-application/${clientId}/${collegeId}`,
        {
          student_id: student?.student_id,
          acyear_id: acyearId,
          service_type_id: typeId,
          service_name: row.service_name,
          service_name_id: activeNames.find((item) => item.service_name === row.service_name)
            ?.service_name_id,
          created_by: user?.user_email || null,
        },
        { headers: auth() }
      );
      await Swal.fire({
        title: "Request submitted",
        text: result.data?.message || "Your request has been submitted.",
        icon: "success",
        confirmButtonColor: "#1976d2",
      });
      setServiceName("");
      setError("");
      loadRequests();
    } catch (e) {
      setError(e.response?.data?.message || "Unable to submit the service request");
    }
  };

  const columns = [
    {
      field: "sno",
      headerName: "S.No",
      width: 80,
      sortable: false,
      valueGetter: (params) => params.api.getRowIndex(params.id) + 1,
    },
    { field: "service_type", headerName: "Service Type", flex: 1, minWidth: 150 },
    { field: "service_name", headerName: "Service Name", flex: 1, minWidth: 150 },
    {
      field: "amount",
      headerName: "Amount",
      width: 120,
      valueFormatter: (params) => `₹${params.value}`,
    },
    {
      field: "apply",
      headerName: "Apply",
      width: 130,
      sortable: false,
      renderCell: ({ row }) => (
        <Button
          size="small"
          variant="contained"
          onClick={() => apply(row)}
          disabled={!student?.student_id || chargesLoading}
          sx={{ color: "#fff", "&:hover": { color: "#fff" } }}
        >
          Apply
        </Button>
      ),
    },
  ];

  return (
    <Box p={1.5}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" mb={2}>
          STUDENT SERVICE REQUEST
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2}>
          <TextField
            select
            fullWidth
            label="Service Type"
            value={typeId}
            disabled={loading}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => {
              setTypeId(e.target.value);
              setServiceName("");
            }}
            sx={{ width: 250 }}
          >
            <option value="">Select service type</option>
            {types.map((row) => (
              <option key={row.service_type_id} value={row.service_type_id}>
                {row.service_type}
              </option>
            ))}
          </TextField>

          {typeId && availableNames.length > 0 && (
            <TextField
              select
              fullWidth
              label="Service Name"
              value={serviceName}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => setServiceName(e.target.value)}
              sx={{ width: 250 }}
            >
              <option value="">Select service name</option>
              {availableNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </TextField>
          )}
        </Stack>
        {typeId && serviceName && !charge && !chargesLoading && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No active charge is set for “{serviceName}” in academic year {academicYearLabel}. Ask
            the college office to add a charge for this year.
          </Alert>
        )}
        <Box sx={{ height: 210, width: "100%" }}>
          <DataGrid
            rows={gridRows}
            columns={columns}
            getRowId={(row) => row.id}
            density="compact"
            hideFooter
            disableRowSelectionOnClick
          />
        </Box>
      </Paper>
      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="h6" mb={2}>
          MY REQUESTED SERVICES
        </Typography>
        <Box sx={{ height: 320, width: "100%" }}>
          <DataGrid
            rows={requestedRows}
            columns={[
              {
                field: "sno",
                headerName: "S.No",
                width: 76,
                sortable: false,
                valueGetter: (params) => params.api.getRowIndex(params.id) + 1,
              },
              { field: "service_type", headerName: "Service Type", flex: 1, minWidth: 140 },
              { field: "service_name", headerName: "Service Name", flex: 1, minWidth: 140 },
              { field: "amount", headerName: "Amount", width: 110 },
              { field: "status", headerName: "Status", width: 120 },
              { field: "year_number", headerName: "Year", width: 80 },
              { field: "semester_number", headerName: "Semester", width: 100 },
              { field: "paid_status", headerName: "Paid Status", width: 120 },
              {
                field: "actions",
                headerName: "Actions",
                width: 190,
                sortable: false,
                renderCell: ({ row }) =>
                  String(row.paid_status).toLowerCase() === "notpaid" &&
                  String(row.fee_book_status).toLowerCase() === "notpaid" ? (
                    <Stack direction="row" spacing={1}>
                      <Button size="small" onClick={() => editRequest(row)}>
                        Edit
                      </Button>
                      <Button color="error" size="small" onClick={() => removeRequest(row)}>
                        Delete
                      </Button>
                    </Stack>
                  ) : null,
              },
            ]}
            getRowId={(row) => row.id}
            density="compact"
            disableRowSelectionOnClick
          />
        </Box>
      </Paper>
    </Box>
  );
}

StudentServiceRequest.propTypes = {};
